import { createSelector } from 'reselect';
import { RootState } from '../../../../types';
import { EntityTypes } from '../../../../constants/entities';
import { ActionStatus, ActionType, IActionBasic } from 'types/domain/action';
import { IEntityReducer } from '../../../reducers/entities';
import { Handles } from 'constants/handles';
import { parseAction, parseActionDAO } from './action';
import { selectFeed } from '../feed';
import {
  ActionHistory,
  ActionHistoryItem,
  selectActionHistory,
  selectPreviewStartActionId,
  selectSimulatedActionHistory,
} from './actionHistory';
import { IModuleAssign, IModuleOut, ModuleType } from 'types/domain/module';
import { IRegisterReducer, RegisterResult } from 'core/slices/register';
import { IQueueItem } from 'core/slices/queue';
import { getFirstAction } from './actionFirst';
import { getEntities } from 'core/selectors/utils/getEntities';
import { Mode } from 'core/slices/session';
import { blameInputs, timeRecompute } from 'core/middleware/perf';
import { answerModuleOut } from './actionEntityModuleOut';

export interface IActionFeed {
  feed: IActionFeedItem[];
  index: number;
  next: boolean;
}

export interface IActionFeedItem {
  actionId?: number;
  actionType?: ActionType;
  sceneId?: number;
  sessionAttemptId?: number;
  courseProgressId?: number;
  actionRegisterId: string;
  verificationRequestId?: number;
  courseNodeId?: number;
  moduleId?: number;
  feedback?: boolean;
}

const entity = (state: RootState) => state.entity.present;
const feed = selectFeed;
const actionHistory = selectActionHistory;
const simulatedActionHistory = selectSimulatedActionHistory;
const mode = (state: RootState) => state.session.mode;
const register = (state: RootState) => state.register;
const registerIndex = (state: RootState) => state.register.index;
const registerIsNext = (state: RootState) => state.register.isNext;

/**
 * Preview history: the synthetic path leading up to an action, followed by what
 * has actually been registered.
 *
 * A module preview opened on a given action (`startActionId`) shows the path to
 * it whatever has been registered:
 * - Before anything is registered the path alone is used, so the feed already
 *   holds the target when `startAction` positions the index on it. Returning
 *   nothing left the target out, the index on the first slide, and the viewer
 *   then snapped the preview back to it — the start node, a blank screen.
 * - Slides on the path keep their place when registered (re-registering moves
 *   an action to the end of the history, e.g. when paging back), but take the
 *   registered item so an answered question still branches.
 *
 * Otherwise (a course preview) the path only fills in what came before the
 * first registered action. If that action isn't on the path the history is used
 * as is — `findIndex` returning -1 used to cut the path's last item and splice
 * the rest in front of it.
 */
const mergeHistory = (
  entity: IEntityReducer,
  moduleId: number,
  simulatedActionHistory: ActionHistoryItem[],
  history: ActionHistoryItem[],
  startActionId: number,
) => {
  const anchor = simulatedActionHistory[simulatedActionHistory.length - 1];
  if (!anchor || entity[EntityTypes.ACTION][anchor.actionId]?.moduleId !== moduleId) return history;

  if (startActionId != null && anchor.actionId === startActionId) {
    const onPath = new Set(simulatedActionHistory.map(i => i.actionId));
    const registered = new Map(history.map(i => [i.actionId, i]));
    return simulatedActionHistory
      .map(i => registered.get(i.actionId) ?? i)
      .concat(history.filter(i => !onPath.has(i.actionId)));
  }

  if (!history.length) return [];
  const first = simulatedActionHistory.findIndex(i => i.actionId === history[0].actionId);
  return first > 0 ? simulatedActionHistory.slice(0, first).concat(history) : history;
};

/**
 * The feed items alone — deliberately independent of `register.index`, so moving
 * between slides doesn't rebuild the list. Every consumer keys off item identity
 * (`Page` in the viewer is memoised on `feedItem`), and rebuilding the array
 * re-rendered every slide in the module on each selection.
 */
const selectActionFeedItems = createSelector(
  [entity, feed, actionHistory, mode, simulatedActionHistory, selectPreviewStartActionId],
  (entity, queue, history, mode, simulated, startActionId) => {
    blameInputs(
      'selectActionFeedItems',
      ['entity', 'feed', 'actionHistory', 'mode', 'simulatedActionHistory', 'previewStartActionId'],
      [entity, queue, history, mode, simulated, startActionId],
    );
    return timeRecompute('selectActionFeedItems', () =>
      reuseFeedItems(buildActionFeedItems(entity, queue, history, mode, simulated, startActionId)),
    );
  },
);

export const selectActionFeed = createSelector(
  [selectActionFeedItems, registerIndex, registerIsNext],
  (feed, index, next): IActionFeed => ({ feed, index, next }),
);

/**
 * Upstream inputs (the entity store above all) change for reasons that don't
 * affect the feed, and each rebuild produces structurally identical but
 * referentially new items. Hand back the previous objects — and the previous
 * array — whenever the content matches, so `React.memo` downstream holds.
 */
let previousFeedItems: IActionFeedItem[] = [];

function reuseFeedItems(next: IActionFeedItem[]): IActionFeedItem[] {
  const previous = previousFeedItems;
  let identical = next.length === previous.length;

  const reconciled = next.map((item, i) => {
    const prev = previous[i];
    if (prev && shallowEqualFeedItem(prev, item)) return prev;
    identical = false;
    return item;
  });

  previousFeedItems = identical ? previous : reconciled;
  return previousFeedItems;
}

function shallowEqualFeedItem(a: IActionFeedItem, b: IActionFeedItem): boolean {
  const keys = Object.keys(a);
  if (keys.length !== Object.keys(b).length) return false;
  return keys.every(key => a[key] === b[key]);
}

// Optional optimized version with sliding window
export const selectActionFeedOptimized = (windowSize: number = 10) =>
  createSelector(
    [entity, feed, actionHistory, register, mode, simulatedActionHistory],
    (entity, queue, history, register, mode, simulatedActionHistory) =>
      parseActionFeed(entity, queue, history, register, mode, simulatedActionHistory, windowSize),
  );

export function parseActionFeed(
  entity: IEntityReducer,
  queue: IQueueItem[],
  history: ActionHistory,
  register: IRegisterReducer,
  mode: Mode,
  simulatedActionHistory,
  windowSize?: number, // Optional window size for optimization
): IActionFeed {
  const fullFeed = buildActionFeedItems(entity, queue, history, mode, simulatedActionHistory);

  return applyIndex(fullFeed, register.index, register.isNext, windowSize);
}

export function buildActionFeedItems(
  entity: IEntityReducer,
  queue: IQueueItem[],
  history: ActionHistory,
  mode: Mode,
  simulatedActionHistory,
  /** The action a module preview was opened on — anchors the synthetic history. */
  previewStartActionId: number = null,
): IActionFeedItem[] {
  const fullFeed = queue.flatMap(item => {
    if (item.feedback) {
      return [
        {
          feedback: true,
          sessionAttemptId: item.sessionAttemptId,
          courseProgressId: item.courseProgressId,
          actionRegisterId: null,
        },
      ];
    } else if (item.moduleId) {
      return nextModule(
        item.sessionAttemptId,
        entity,
        item.moduleId,
        mode === Mode.ADMIN
          ? simulatedActionHistory
          : mode === Mode.PREVIEW
          ? mergeHistory(
              entity,
              item.moduleId,
              simulatedActionHistory,
              history[item.sessionAttemptId] ?? [],
              previewStartActionId,
            )
          : history[item.sessionAttemptId] ?? [],
      );
    } else return [{ courseNodeId: item.courseNodeId, actionRegisterId: null }];
  });

  // In edit (ADMIN) mode the selected action can be a branch target — e.g. a
  // correct/wrong answer slide in an assessment — that doesn't lie on the linear
  // feed path (assessments build their feed from module.order, DEFAULT chain only).
  // The learner/preview feed adds such actions to the queue as they're reached;
  // in edit mode there's no traversal, so add the selected action here so it can be
  // navigated to and loaded like any other slide.
  if (mode === Mode.ADMIN && simulatedActionHistory.length) {
    const selected = simulatedActionHistory[simulatedActionHistory.length - 1];
    if (selected?.actionId && !fullFeed.some(item => item.actionId === selected.actionId)) {
      const selectedAction = entity[EntityTypes.ACTION]?.[selected.actionId];
      // The selected action must belong to a module the queue is actually
      // showing. On a module switch the previously selected action lingers for
      // a beat, and appending it unconditionally injected a slide from the old
      // module into the new module's feed — the "previous action is still
      // showing" symptom.
      const queuedModuleIds = new Set(queue.map(item => item.moduleId).filter(Boolean));
      const belongsToFeed = !queuedModuleIds.size || queuedModuleIds.has(selectedAction?.moduleId);

      if (selectedAction && selectedAction.status !== ActionStatus.DELETED && belongsToFeed)
        fullFeed.push({
          actionId: selected.actionId,
          actionType: selectedAction.type,
          sceneId: selectedAction.sceneId,
          moduleId: selectedAction.moduleId,
          actionRegisterId: null,
        });
    }
  }

  return fullFeed;
}

function applyIndex(fullFeed: IActionFeedItem[], index: number, next: boolean, windowSize?: number): IActionFeed {
  // If windowSize is provided, use sliding window optimization
  if (windowSize && windowSize > 0) {
    const startIndex = Math.max(0, index - Math.floor(windowSize / 2));
    const endIndex = Math.min(fullFeed.length, startIndex + windowSize);

    const optimizedFeed = fullFeed.slice(startIndex, endIndex);
    const adjustedIndex = Math.min(index - startIndex, optimizedFeed.length - 1);

    return {
      feed: optimizedFeed,
      index: Math.max(0, adjustedIndex),
      next,
    } as IActionFeed;
  }

  // Default behavior: return full feed
  return {
    feed: fullFeed,
    index,
    next,
  } as IActionFeed;
}

function processModOut(entity, modOut: IModuleOut) {
  if (!modOut) return null;

  return parseAction(entity, modOut.targetId);
}

/**
 * The out an *answered* branching action takes. A question — a quiz/assessment
 * slide, or any action with correct entities — routes on the answer: an
 * author-wired per-entity handle first, then correct → PASS / wrong → FAIL /
 * timed out → TIME. This mirrors what `handleRegister`
 * (sagas/product/module/action) does when the answer is registered; without it
 * the feed rebuilds the linear DEFAULT/`module.order` path, so the branch target
 * the saga navigated to isn't in the feed and the learner is carried on to the
 * next question instead. Unanswered (or unbranched) actions fall through to the
 * linear path as before.
 */
function answeredModOut(action: IActionBasic, answered?: ActionHistoryItem): IModuleOut {
  if (!answered) return null;

  const entityOut = (answered.selectedEntityIds || []).map(entityId => answerModuleOut(action, entityId)).find(Boolean);
  if (entityOut) return entityOut;

  switch (answered.result) {
    case RegisterResult.PASS:
      return action.outs[Handles.PASS] || null;
    case RegisterResult.FAIL:
      return action.outs[Handles.FAIL] || null;
    case RegisterResult.TIME:
      return action.outs[Handles.TIME] || null;
    default:
      return null;
  }
}

/**
 * Where a branch leg rejoins the module's normal path. Once the answer slide
 * (and anything hanging off it) is exhausted, the feed carries on from the
 * question that branched — its child, else its DEFAULT out — so the learner
 * continues down the path as if they'd never left it. Assessments resolve this
 * through `order` instead, which is checked first.
 */
function rejoinAction(entity: IEntityReducer, resume: IActionBasic, future: IActionFeedItem[]): IActionBasic {
  const nextId = resume.childId || resume.outs[Handles.DEFAULT]?.targetId;

  if (!nextId || future.some(f => f.actionId === nextId)) return null;

  return parseAction(entity, nextId);
}

/**
 * The question whose answer branch the end of `history` is still on, if any.
 *
 * The feed of a module with history carries on from its last slide alone, so on
 * an answer slide it no longer knows the question it branched from — an answer
 * slide with nowhere of its own to go then ended the module. Walk back to the
 * nearest answered question whose branch was taken; unless its path has already
 * been rejoined, that's the question to resume from.
 */
function branchOrigin(entity: IEntityReducer, history: ActionHistoryItem[]): IActionBasic {
  for (let i = history.length - 2; i >= 0; i--) {
    const question = parseAction(entity, history[i].actionId);
    const branch = question ? answeredModOut(question, history[i]) : null;
    if (!branch) continue;

    const later = history.slice(i + 1).map(h => h.actionId);
    if (!later.includes(branch.targetId)) continue;

    const rejoinId = question.childId || question.outs[Handles.DEFAULT]?.targetId;
    return rejoinId && later.includes(rejoinId) ? null : question;
  }

  return null;
}

function addActionToFuture(
  sessionAttemptId: number,
  entity: IEntityReducer,
  action: IActionBasic,
  future: IActionFeedItem[],
  history: ActionHistoryItem[],
  first: boolean,
  order?: number[],
  /** The question a branch came from, to rejoin its path once this leg ends. */
  resume?: IActionBasic,
) {
  if (!action || action.status === ActionStatus.DELETED) return;
  if (!first && future.some(f => f.actionId === action.id) && !order) return;

  // Limit future actions to prevent excessive memory usage
  //if (future.length >= 10) return;

  if (!first)
    future.push({
      actionId: action.id,
      sceneId: action.sceneId,
      actionType: action.type,
      moduleId: action.moduleId,
      sessionAttemptId: sessionAttemptId,
      actionRegisterId: null,
    });

  const answered = [...history].reverse().find(h => h.actionId === action.id);
  const branchOut = answeredModOut(action, answered);
  // Never follow a branch back into a slide the feed already holds — a loop
  // between two answers would otherwise recurse forever.
  const branch = branchOut && !future.some(f => f.actionId === branchOut.targetId) ? branchOut : null;
  const next = processModOut(entity, branch || action.outs[Handles.DEFAULT]);

  if (next && next.status > -1 && next.id > 0)
    // On a branch leg `order` is carried through, and the branching question is
    // remembered as the action to rejoin from, so the feed returns to the normal
    // path (the question's place in the order, else its child) after the answer.
    return addActionToFuture(
      sessionAttemptId,
      entity,
      next,
      future,
      history,
      false,
      branch ? order : undefined,
      branch ? action : resume,
    );

  const rejoin = resume ? rejoinAction(entity, resume, future) : null;

  if (order) {
    let currentIndex = order.indexOf(action.id);
    if (currentIndex === -1) {
      // Off-order slide (an answer branch): resume from the question that
      // branched, falling back to the last in-order action already visited.
      const lastHistoryInOrder = [...history].reverse().find(h => order.includes(h.actionId));
      currentIndex = resume
        ? order.indexOf(resume.id)
        : lastHistoryInOrder
        ? order.indexOf(lastHistoryInOrder.actionId)
        : -1;
    }
    if (currentIndex !== -1 && currentIndex + 1 < order.length)
      addActionToFuture(
        sessionAttemptId,
        entity,
        parseAction(entity, order[currentIndex + 1]),
        future,
        history,
        false,
        order,
      );
  } else if (action.childId && !future.some(f => f.actionId === action.childId)) {
    const child = parseActionDAO(entity, action.childId);
    if (child && child.status > -1 && child.id > 0)
      return addActionToFuture(sessionAttemptId, entity, child, future, history, false, undefined, resume);
  } else if (rejoin) {
    return addActionToFuture(sessionAttemptId, entity, rejoin, future, history, false, order);
  } else {
    // Fallback: try to continue from the grandparent's next child
    const parent = history[history.length - 2];

    if (parent) {
      const parentAction = parseAction(entity, parent.actionId);
      const nextId = parentAction.outs[Handles.DEFAULT]?.targetId;
      if (nextId && nextId !== action.id && !future.some(f => f.actionId === nextId))
        addActionToFuture(sessionAttemptId, entity, parseAction(entity, nextId), future, history, false);
    }
  }
}

function nextModule(
  sessionAttemptId: number,
  entity: IEntityReducer,
  moduleId: number,
  history: ActionHistoryItem[],
): IActionFeedItem[] {
  const module = entity[EntityTypes.MODULE][moduleId] as IModuleAssign;

  if (!module) {
    console.warn('[actionFeed] nextModule — module not in entity store yet', { moduleId, sessionAttemptId });
    return [];
  }

  if ([ModuleType.YOUTUBE, ModuleType.SCORM, ModuleType.WEBSITE].includes(module.type)) {
    return [
      {
        moduleId: module.id,
        sessionAttemptId: module.sessionAttemptId || sessionAttemptId,
        actionRegisterId: null,
      },
    ];
  }

  let future = [];

  if (history.length && ![ModuleType.ASSESSMENT, ModuleType.ASSESSMENT_RANDOM].includes(module.type)) {
    future = history.map(historyItem => {
      return {
        ...historyItem,
        moduleId: module.id,
        sessionAttemptId: sessionAttemptId,
        actionRegisterId: historyItem.actionRegisterId ?? null,
      };
    });

    addActionToFuture(
      sessionAttemptId,
      entity,
      parseAction(entity, future[future.length - 1].actionId),
      future,
      history,
      true,
      module.order,
      branchOrigin(entity, history),
    );
    if ([ModuleType.ASSESSMENT, ModuleType.ASSESSMENT_RANDOM].includes(module.type))
      future.push({
        moduleId: module.id,
        sessionAttemptId: sessionAttemptId,
        feedback: true,
      });
  } else if (module.order) {
    addActionToFuture(
      sessionAttemptId,
      entity,
      parseAction(entity, module.order[0]),
      future,
      history,
      false,
      module.order,
    );
    if ([ModuleType.ASSESSMENT, ModuleType.ASSESSMENT_RANDOM].includes(module.type))
      future.push({
        moduleId: module.id,
        sessionAttemptId: sessionAttemptId,
        feedback: true,
      });
  } else if (module.actionId)
    addActionToFuture(sessionAttemptId, entity, parseAction(entity, module.actionId), future, history, false);
  else {
    const actions = getEntities(entity, EntityTypes.ACTION, 'moduleId', module.id).map(a => parseAction(entity, a.id));

    const action = getFirstAction(actions);

    if (action) addActionToFuture(sessionAttemptId, entity, parseAction(entity, action.id), future, history, false);
  }

  return future;
}
